import SearchField from '@shared/components/core/search/SearchField';
import MacroListing from '@shared/components/macro/MacroListing';
import AssociatedProgramsListing from '@shared/components/profile/listing/AssociatedProgramsListing';
import DictationListing from '@shared/components/profile/listing/DictationListing';
import HotkeysListing from '@shared/components/profile/listing/HotkeysListing';
import KeyCommandListing from '@shared/components/profile/listing/KeyCommandsListing';
import TalkbackListing from '@shared/components/profile/listing/TalkbackListing';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useProfilesStore } from '@shared/hooks/state/ProfilesContainer';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

export interface IProfilePreviewPageContentProps {
    isDeepLink?: boolean;
}
let lastShareCode: string | null = null;
let lastProfileId: string | null = null;
export default function ProfilePreviewPageContent({ isDeepLink = false }: IProfilePreviewPageContentProps) {
    const { shareCode, profileId } = useParams();
    const { t } = useTranslation();
    const macros = useEditProfileStore((state) => state.macros);
    const hotkeys = useEditProfileStore((state) => state.hotkeys);
    const audioEvents = useEditProfileStore((state) => state.audioEvents);
    const dictationCommands = useEditProfileStore((state) => state.dictationCommands);
    const commandKeyMappings = useEditProfileStore((state) => state.commandKeyMappings);
    const associatedPrograms = useEditProfileStore((state) => state.associatedPrograms);
    const name = useEditProfileStore((state) => state.name);
    const [filter, setFilter] = useState<string>('');
    const [loadingError, setLoadingError] = useState<boolean>(false);

    const filteredMacros = useMemo(() => {
        if (loadingError) return [];
        return macros.filter((m) => {
            const macroName = m.name?.toLowerCase() || t('common:noName');
            const commandMatches = m.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return commandMatches || macroName.includes(filter) || !filter;
        });
    }, [filter, macros, loadingError, t]);

    const filteredHotkeys = useMemo(() => {
        if (loadingError) return [];
        return hotkeys.filter((h) => {
            const hotkeyName = h.displayName?.toLowerCase().includes(filter);
            const hotkeyCommandMatches = h.command?.toLowerCase().includes(filter);
            return hotkeyCommandMatches || hotkeyName || !filter;
        });
    }, [filter, hotkeys, loadingError]);

    const filteredTalkback = useMemo(() => {
        if (loadingError) return [];
        return audioEvents.filter((a) => {
            const talkbackName = a.outputSpeech?.toLowerCase().includes(filter);
            const talkbackCommandMatches = a.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return talkbackCommandMatches || talkbackName || !filter;
        });
    }, [filter, audioEvents, loadingError]);

    const filteredDictation = useMemo(() => {
        if (loadingError) return [];
        return dictationCommands.filter((d) => {
            const dictationName = d?.toLowerCase().includes(filter);
            return dictationName || !filter;
        });
    }, [filter, dictationCommands, loadingError]);

    const filteredKeyCommands = useMemo(() => {
        if (loadingError) return [];
        return commandKeyMappings.filter((k) => {
            const keyCommandName = k.displayName?.toLowerCase().includes(filter);
            const keyCommandCommandMatches = k.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return keyCommandCommandMatches || keyCommandName || !filter;
        });
    }, [filter, commandKeyMappings, loadingError]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setFilter(query);
    };

    const handleClearSearch = () => {
        setFilter('');
    };
    useEffect(() => {
        if (lastShareCode === shareCode && shareCode != null) {
            return;
        }
        if (lastProfileId === profileId && profileId != null && !shareCode) {
            return;
        }
        lastShareCode = shareCode ?? null;
        lastProfileId = profileId ?? null;

        const loadProfile = async () => {
            const editProfileStore = useEditProfileStore.getState();
            editProfileStore.setIsPreview(true);
            if (shareCode) {
                const result = await editProfileStore.loadPreview(shareCode);
                if (result.resultType !== 'Ok') {
                    editProfileStore.setDefaultProfile('en-US');
                    setLoadingError(true);
                }
            } else if (profileId) {
                const profile = useProfilesStore.getState().allProfiles.find((p) => p.id === profileId);
                if (profile) {
                    editProfileStore.updateProfileState({
                        ...profile,
                        userId: undefined,
                    });
                } else {
                    editProfileStore.loadPublicPreview(profileId);
                }
            }
        };
        loadProfile();
    }, [profileId, shareCode]);

    function hasKeyCommands(commandKeyMappings: KeyCommandDisplayMapping[]): boolean {
        return commandKeyMappings.some((item) => item.commands.length > 0);
    }

    if (loadingError) {
        return (
            <div>
                <p>{t('common:profile.previewLoadError')}</p>
            </div>
        );
    }
    return (
        <>
            <SearchField value={filter} onChange={handleSearchChange} onClear={handleClearSearch} />

            {macros.length > 0 && <MacroListing macros={filteredMacros} />}
            {hotkeys.length > 0 && <HotkeysListing hotkeys={filteredHotkeys} />}
            {audioEvents.length > 0 && <TalkbackListing audioEvents={filteredTalkback} />}
            {dictationCommands.length > 0 && <DictationListing dictationCommands={filteredDictation} />}

            {hasKeyCommands(commandKeyMappings) && <KeyCommandListing commandKeyMappings={filteredKeyCommands} />}
            {associatedPrograms.length > 0 && (
                <AssociatedProgramsListing profileName={name} programNames={associatedPrograms} />
            )}
        </>
    );
}
